import { useState, useEffect, type ComponentType } from 'react';
import { useParams, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import type { ArcheOpenedSource } from '@fias/analytics-types';
import { analytics } from '@/lib/analytics';
import { ArcheLayout, ArcheDisplayMode } from '../components/ArcheLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { MakeARequestPage } from './make-a-request';
import { SingleAIPage } from './single-ai';
import { ImageGeneratorArchePage } from './image-generator';
import { MultiAIPage } from './multi-ai';
import { LearnAboutFiasChatPage } from './learn-fias-chat';
import { LearnAnythingPage } from './learn-anything';
import { ContactBookArchePage } from './contact-book';
import { ChatArchePage } from './chat';
import { GuildArchePage } from './guilds';
import { DfeArchePage } from './dfe';
import { LitSquadArchePage } from './litsquad';
import { DfeInventoryTransitionArchePage } from './dfe-inventory-transition';
import { FavoritesArchePage } from './favorites';
import { GameRewardsArchePage } from './game-rewards';
import { DfeGameStateArchePage } from './dfe-game-state';
import { PollsArchePage } from './polls';
import { PluginBuilderArchePage } from './plugin-builder';
import { CraftingArchePage } from './crafting';
import { MiningArchePage } from './mining';
import { LearnAboutFiasArchePage } from './learn-about-fias';
import { LiquidityCachesArchePage } from './liquidity-caches';
import { FiardleArchePage } from './fiardle';
import { PluginRenderer } from '../components/PluginRenderer';
import { CalendarArchePage } from './calendar';
import { SocialMediaArchePage } from './social-media';
import { IconGeneratorArchePage } from './icon-generator';
import { DfeAvatarsArchePage } from './dfe-avatars';
import { DfeIconsArchePage } from './dfe-icons';
import { LitcraftAddressablesArchePage } from './litcraft-addressables';
import { LitLegionArchePage } from './lit-legion';
import { BusinessArchePage } from './businesses';
import { ImageEditorArchePage } from './image-editor';
import { BetaAccessArchePage } from './beta-access';
import { TodoListsArchePage } from './todo-lists';
import { DocumentSignArchePage } from './document-sign';
import { CustomerSupportArchePage } from './customer-support';
import { VoiceStudioArchePage } from './voice-studio';
import { LeadsArchePage } from './leads';
import { EmailArchePage } from './email';
import { TravelArchePage } from './travel';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';

/**
 * Determines the display mode for the arche layout based on metadata, auth state, and embed flag.
 *
 * Security: Embed mode is only allowed for arches that are:
 * - Public (isPublic=true)
 * - Read-only (no workflow)
 * This prevents authenticated actions from being performed in embedded iframes.
 */
function determineDisplayMode(
  metadata: ArcheMetadata | null,
  isAuthenticated: boolean,
  isEmbed: boolean,
): ArcheDisplayMode {
  // Embed mode is only allowed for embeddable arches (public + no workflow)
  // This is a security measure to prevent authenticated actions in iframes
  if (isEmbed && metadata?.isEmbeddable) {
    return 'embed';
  }

  if (!metadata) {
    return 'normal'; // Default to normal while loading
  }

  // Full-screen mode takes precedence
  if (metadata.isFullScreen) {
    return 'fullscreen';
  }

  // Public mode for unauthenticated users on public arches
  if (metadata.isPublic && !isAuthenticated) {
    return 'public';
  }

  return 'normal';
}

/**
 * Error component shown when embed mode is requested but not allowed
 */
function EmbedNotAllowedError() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Card className="max-w-md mx-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Embedding Not Allowed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            This arche cannot be embedded. Only public, read-only arches can be embedded in iframes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Generic arche page for dynamic arches (plugins, etc.)
 */
function GenericArchePage({ archeId }: { archeId: string }) {
  const { isAuthenticated, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [metadata, setMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);

  // Check for embed mode via query parameter
  const isEmbed = searchParams.get('embed') === 'true';

  // Security: Clear any existing authentication when in embed mode
  // This prevents authenticated actions from being performed in embedded iframes
  useEffect(() => {
    if (isEmbed && isAuthenticated) {
      signOut();
    }
  }, [isEmbed, isAuthenticated, signOut]);

  useEffect(() => {
    let cancelled = false;

    async function fetchMetadata() {
      try {
        const result = await api.getArcheMetadata(archeId);
        if (!cancelled) {
          setMetadata(result);
        }
      } catch {
        if (!cancelled) {
          setMetadata(null);
        }
      } finally {
        if (!cancelled) {
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  // Check if embed mode was requested but not allowed
  const embedDenied = isEmbed && !metadataLoading && metadata && !metadata.isEmbeddable;

  if (embedDenied) {
    return <EmbedNotAllowedError />;
  }

  // Plugin arches render in a sandboxed iframe
  // Use metadata.archId (the resolved real ID) instead of the URL param which may be a slug
  if (!metadataLoading && metadata?.renderMode === 'plugin') {
    const displayMode = determineDisplayMode(metadata, isAuthenticated, isEmbed);
    return (
      <ArcheLayout displayMode={displayMode}>
        <PluginRenderer archeId={metadata.archId} />
      </ArcheLayout>
    );
  }

  // Non-plugin generic arches — show "not available"
  if (!metadataLoading) {
    return (
      <ArcheLayout displayMode="normal">
        <div className="p-8 max-w-3xl mx-auto">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Not Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">This arche is not available.</p>
            </CardContent>
          </Card>
        </div>
      </ArcheLayout>
    );
  }

  return null;
}

function inferArcheOpenedSource(state: unknown, referrer: string): ArcheOpenedSource {
  // react-router-dom v7 carries an opaque `state` object on navigations.
  // We honor an explicit { archeOpenSource: ArcheOpenedSource } if present
  // so that future navigation call sites can attribute properly.
  if (state && typeof state === 'object' && 'archeOpenSource' in state) {
    const raw = (state as { archeOpenSource: unknown }).archeOpenSource;
    if (raw === 'directory' || raw === 'search' || raw === 'recommended' || raw === 'direct_link') {
      return raw;
    }
  }
  // Heuristic fallback: arrived from /marketplace or /arches → directory.
  // No referrer (typed URL or external link) → direct_link.
  try {
    const u = referrer ? new URL(referrer) : null;
    if (
      u &&
      (u.pathname === '/marketplace' ||
        u.pathname.startsWith('/marketplace/') ||
        u.pathname === '/arches')
    ) {
      return 'directory';
    }
  } catch {
    // ignore malformed referrers
  }
  return 'direct_link';
}

/**
 * Map of archeId → page component for internal arches with dedicated UIs.
 * Adding a new internal arche means adding an import above and one entry
 * here; no other edits to this file are needed.
 *
 * Plugin arches (and any unmapped ID) fall through to `GenericArchePage`,
 * which is the iframe-based renderer.
 *
 * Long-term, this registry is a candidate for code-splitting via
 * `React.lazy` per arche, and for being driven by the `arches` DB table
 * directly so adding an arche requires no edits here at all. See
 * docs/audits/social-media-modularity-audit.md §10 PW-C.
 */
const INTERNAL_ARCHE_COMPONENTS: Record<string, ComponentType> = {
  arche_guilds: GuildArchePage,
  arche_chat: ChatArchePage,
  arche_make_a_request: MakeARequestPage,
  arche_single_ai: SingleAIPage,
  arche_image_generator: ImageGeneratorArchePage,
  arche_multi_ai: MultiAIPage,
  arche_learn_fias_chat: LearnAboutFiasChatPage,
  arche_learn_anything: LearnAnythingPage,
  arche_dfe: DfeArchePage,
  arche_litsquad: LitSquadArchePage,
  arche_dfe_inventory_transition: DfeInventoryTransitionArchePage,
  arche_contact_book: ContactBookArchePage,
  arche_favorites: FavoritesArchePage,
  arche_game_rewards: GameRewardsArchePage,
  arche_dfe_game_state: DfeGameStateArchePage,
  arche_polls: PollsArchePage,
  arche_plugin_builder: PluginBuilderArchePage,
  arche_crafting: CraftingArchePage,
  arche_dfe_mining: MiningArchePage,
  arche_learn_about_fias: LearnAboutFiasArchePage,
  arche_liquidity_caches: LiquidityCachesArchePage,
  arche_fiardle: FiardleArchePage,
  arche_calendar: CalendarArchePage,
  arche_social_media: SocialMediaArchePage,
  arche_icon_generator: IconGeneratorArchePage,
  arche_lit_legion: LitLegionArchePage,
  arche_businesses: BusinessArchePage,
  arche_image_editor: ImageEditorArchePage,
  arche_beta_access: BetaAccessArchePage,
  arche_customer_support: CustomerSupportArchePage,
  arche_dfe_avatars: DfeAvatarsArchePage,
  arche_dfe_icons: DfeIconsArchePage,
  arche_litcraft_addressables: LitcraftAddressablesArchePage,
  arche_todo_lists: TodoListsArchePage,
  arche_leads: LeadsArchePage,
};

/**
 * Map of archeId → external URL for arches that are redirects to other
 * top-level routes rather than embedded components.
 */
const ARCHE_REDIRECTS: Record<string, string> = {
  arche_marketplace: '/marketplace',
};

export function ArcheInteractionPage() {
  const { id: archeId } = useParams<{ id: string }>();
  const location = useLocation();

  // Emit arche_opened on entry, arche_left on unmount/swap. Time-spent
  // inside is the primary dashboard signal for arche engagement.
  useEffect(() => {
    if (!archeId) return;
    const source = inferArcheOpenedSource(location.state, document.referrer);
    analytics.archeOpened(archeId, source);
    const enteredAt = Date.now();
    return () => {
      analytics.archeLeft(archeId, Date.now() - enteredAt);
    };
  }, [archeId, location.state]);

  if (!archeId) {
    return <Navigate to="/a/arche_make_a_request" replace />;
  }

  // Redirects to other top-level routes
  const redirectTo = ARCHE_REDIRECTS[archeId];
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  // Internal arches with dedicated page components
  const Component = INTERNAL_ARCHE_COMPONENTS[archeId];
  if (Component) {
    return <Component />;
  }

  if (archeId === 'arche_voice_studio') {
    return <VoiceStudioArchePage />;
  }

  if (archeId === 'arche_document_sign') {
    return <DocumentSignArchePage />;
  }

  if (archeId === 'arche_email') {
    return <EmailArchePage />;
  }

  if (archeId === 'arche_travel') {
    return <TravelArchePage />;
  }

  // All other arches (plugins, etc.) use the generic page
  return <GenericArchePage archeId={archeId} />;
}

export default ArcheInteractionPage;
